import classNames from 'classnames';
import { useI18n } from '@/libraries/contexts/i18n-context';
import React from 'react';
import Image from '@/components/atoms/image/image';
import Typography from '@/components/atoms/typography/typography';
import AnchorLink from '@/components/molecules/anchor-link/anchor-link';
import Button from '@/components/molecules/button/button';
import IconLabel from '@/components/molecules/icon-label/icon-label';
import StarRating from '@/components/molecules/star-rating/star-rating';
import { TagVariant } from '@/components/molecules/tag/tag';
import TagList from '@/components/organisms/tag-list/tag-list';
import Modal, { ModalProps } from '@/components/utilities/modal/modal';
import useCurrencyFormatter from '@/libraries/formatters/use-currency-formatter';
import styles from './hotel-map-details-modal.module.scss';

export interface HotelMapDetailsModalProps
  extends Omit<ModalProps, 'children'> {
  /** The aspect ratio to use for the image */
  aspectRatio?: Content.Properties.AspectRatio;
  /** The description to display */
  description?: string;
  /** Image to display */
  image?: {
    altText: string;
    url: string;
  };
  /** Whether the modal is open or not */
  isOpen?: boolean;
  /** The name to display */
  name?: string;
  /** The callback to close the modal */
  onClose?: () => void;
  /** Rating to display in the star rating */
  rating?: number;
  /** Tag array to display for the hotel */
  tags?: {
    text: string;
    variant: TagVariant;
  }[];
  /** From price per person to show on the card */
  totalPricePerPerson?: number;
  /** Type of the property */
  type?: Content.Components['HotelType'];
  /** The url to link to */
  url?: string;
}

const HotelMapDetailsModal = ({
  aspectRatio,
  description,
  image,
  isOpen = false,
  name,
  onClose,
  rating,
  tags,
  totalPricePerPerson,
  type,
  url,
  ...rest
}: HotelMapDetailsModalProps) => {
  const { t } = useI18n();
  const currencyFormatter = useCurrencyFormatter();

  const handleCloseModal = () => {
    onClose?.();
  };

  const types: Record<
    Exclude<Content.Components['HotelType'], 'Undefined'>,
    string
  > = {
    Apartment: t('items.common.words.apartment'),
    Chalet: t('items.common.words.chalet'),
    Hotel: t('items.common.words.hotel'),
    TransportHotel: t('items.common.words.transport'),
  };

  const iconName =
    type === 'TransportHotel'
      ? 'PlaneTakeOff'
      : type && type !== 'Undefined'
        ? type
        : undefined;

  const hasIcon = !!iconName;

  if (!isOpen) return null;

  return (
    <Modal
      {...rest}
      className={styles.component}
      isOpen={isOpen}
      onClickOutside={handleCloseModal}
      setIsOpen={handleCloseModal}
    >
      <div className={styles.curtain}>
        <div className={styles.container}>
          <div className={styles.modalActions}>
            <Button
              aria-label={t('items.common.words.cancel')}
              icon="Close"
              iconSize={16}
              onClick={handleCloseModal}
              variant="tertiary"
            />
          </div>

          {image && (
            <div
              className={classNames(styles.imageWrapper, {
                [styles.isThreeByFour]: aspectRatio === 'threeByFour',
                [styles.isSixteenByNine]: aspectRatio === 'sixteenByNine',
              })}
            >
              <Image
                alt={image.altText}
                className={styles.image}
                src={image.url}
                srcSet={{
                  tablet: { width: 324 },
                }}
                unoptimized
                width={666}
              />
            </div>
          )}

          <div className={styles.detailsWrapper}>
            <div className={styles.details}>
              {(type || name || rating) && (
                <div className={styles.detailsHeader}>
                  {hasIcon && type && (
                    <IconLabel icon={iconName} text={types[type]} />
                  )}

                  {name && (
                    <Typography
                      className={styles.name}
                      tag="div"
                      tagStyle="headingMedium"
                    >
                      {name}
                    </Typography>
                  )}

                  {(rating || totalPricePerPerson) && (
                    <div className={styles.detailsContent}>
                      {rating && <StarRating rating={rating} />}

                      {totalPricePerPerson && (
                        <div className={styles.fromPrice}>
                          <Typography
                            className={styles.name}
                            tagStyle="priceSmall"
                          >
                            {t('items.common.words.from')}
                          </Typography>

                          <Typography
                            className={styles.name}
                            tagStyle="priceMedium"
                          >
                            {currencyFormatter(totalPricePerPerson)}
                          </Typography>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {tags && tags.length > 0 && <TagList tags={tags} />}

              {description && (
                <Typography
                  className={styles.description}
                  tagStyle="bodyMedium"
                >
                  {description}
                </Typography>
              )}

              {url && (
                <AnchorLink
                  className={styles.link}
                  icon="ArrowRight"
                  text={t('items.hotelCard.viewHotelDetails')}
                  url={url}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

HotelMapDetailsModal.displayName = 'HotelMapDetailsModal';

export default HotelMapDetailsModal;
